import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Brain,
  Bot,
  MessageSquare,
  Save,
  Loader2,
  Plus,
  Trash2,
  Check,
  X,
  Sparkles,
  Power,
  PowerOff,
  RotateCcw,
  Eye,
  EyeOff,
  Zap,
  History,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Lock,
  Cpu,
  Camera,
  FileCheck,
  Languages,
  Building2,
  HardDriveDownload,
  BookOpen,
  DollarSign,
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import ClaudeCostTab from '../components/ClaudeCostTab';

// Fallback only — the live list comes from GET /admin/intel/providers, which is
// the single source of truth (backend PROVIDERS registry).
const FALLBACK_PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic (Claude)', needs_key: true, needs_endpoint: false, default_endpoint: '' },
  { value: 'openai', label: 'OpenAI (GPT)', needs_key: true, needs_endpoint: false, default_endpoint: '' },
  { value: 'google', label: 'Google (Gemini)', needs_key: true, needs_endpoint: false, default_endpoint: '' },
  { value: 'kimi', label: 'Moonshot (Kimi)', needs_key: true, needs_endpoint: true, default_endpoint: 'https://api.moonshot.cn/v1' },
  { value: 'custom', label: 'Custom (OpenAI-compatible)', needs_key: true, needs_endpoint: true, default_endpoint: '' },
  { value: 'claude_local', label: 'Claude Local (server CLI)', needs_key: false, needs_endpoint: false, default_endpoint: '' },
];

// Fallback model IDs per provider.
//
// These are only shown when the provider's own API cannot be reached — the
// dropdown fetches the live list first. Treat this as a safety net, not the
// source of truth: it is what went stale and left the page offering retired
// Claude 4 models.
const SUGGESTED_MODELS = {
  anthropic: [
    { id: 'claude-opus-5', label: 'Claude Opus 5 (Most Capable)' },
    { id: 'claude-sonnet-5', label: 'Claude Sonnet 5 (Balanced)' },
    { id: 'claude-fable-5', label: 'Claude Fable 5' },
    { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Fast & Cheap)' },
  ],
  openai: [
    { id: 'gpt-4o', label: 'GPT-4o (Most Capable)' },
    { id: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Cheap)' },
    { id: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { id: 'o3', label: 'o3 (Reasoning)' },
    { id: 'o3-mini', label: 'o3 Mini (Reasoning, Cheap)' },
  ],
  // Google blocks its list-models endpoint from this server's datacenter range,
  // so the Gemini dropdown always falls back here. Prefer the -latest aliases:
  // they track Google's current model and cannot go stale the way a pinned id
  // does (gemini-2.5-pro was retired out from under this list).
  google: [
    { id: 'gemini-pro-latest', label: 'Gemini Pro (Latest)' },
    { id: 'gemini-flash-latest', label: 'Gemini Flash (Latest, Fast)' },
    { id: 'gemini-flash-lite-latest', label: 'Gemini Flash-Lite (Latest, Cheap)' },
    { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview' },
    { id: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash' },
    { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  ],
  kimi: [
    { id: 'kimi-k2.5', label: 'Kimi K2.5 (Latest, Multimodal)' },
    { id: 'kimi-k2.5-preview', label: 'Kimi K2.5 Preview' },
    { id: 'kimi-k2-thinking', label: 'Kimi K2 Thinking (Reasoning)' },
    { id: 'moonshot-v1-128k', label: 'Moonshot V1 128K (Legacy)' },
    { id: 'moonshot-v1-32k', label: 'Moonshot V1 32K (Legacy)' },
    { id: 'moonshot-v1-8k', label: 'Moonshot V1 8K (Legacy)' },
  ],
  custom: [],
  // Claude Code CLI aliases — always resolve to the current model, so they
  // cannot go stale. Full model ids are also accepted via "Custom Model ID".
  claude_local: [
    { id: 'opus', label: 'Opus (latest)' },
    { id: 'sonnet', label: 'Sonnet (latest)' },
    { id: 'haiku', label: 'Haiku (latest)' },
  ],
};

const AGENT_LABELS = {
  public: { name: 'Brandon', subtitle: 'External Agent', description: 'Assists visitors on the Verify LOI page. No authentication required.', icon: '🌐' },
  user: { name: 'Amanda', subtitle: 'Internal Agent', description: 'Helps authenticated users navigate the platform and manage their workflows.', icon: '👩‍💼' },
  intel: { name: 'Intel', subtitle: 'Intelligence Agent', description: 'Deep LOI forensic analysis for administrators. Has access to Amanda & Brandon conversations for continuous learning.', icon: '🔍' },
  trados: { name: 'Trados', subtitle: 'Operations Agent', description: 'Internal AI operations agent. Analyzes platform data, reviews prompts, and suggests improvements.', icon: '⚙️' },
};

const AGENT_ORDER = ['user', 'public', 'intel', 'trados'];

const AdminIntelSettings = () => {
  usePageTitle('Intel Settings');
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'models');
  const [loading, setLoading] = useState(true);

  // Global state
  const [enabled, setEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [intelPaidOnly, setIntelPaidOnly] = useState(false);
  const [togglingPaidOnly, setTogglingPaidOnly] = useState(false);

  // Models
  const [models, setModels] = useState([]);
  const [providers, setProviders] = useState(FALLBACK_PROVIDERS);
  const [showModelForm, setShowModelForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [modelForm, setModelForm] = useState({ name: '', provider: 'anthropic', model_id: '', api_key: '', api_endpoint: '' });
  const [modelSaving, setModelSaving] = useState(false);
  const [testingModel, setTestingModel] = useState(null);
  const [testedModels, setTestedModels] = useState({});  // { modelId: 'success' | 'failed' }
  const [showApiKeys, setShowApiKeys] = useState({});
  const [fetchingProviderModels, setFetchingProviderModels] = useState(false);
  const [providerModels, setProviderModels] = useState([]);  // fetched from API

  // Agents
  const [agents, setAgents] = useState({});
  const [agentSaving, setAgentSaving] = useState(null);
  const [localMaxTokens, setLocalMaxTokens] = useState({});

  // Prompts
  const [activePromptAgent, setActivePromptAgent] = useState('user');
  const [promptForm, setPromptForm] = useState({ system_prompt: '', knowledge_base: '' });
  const [promptSaving, setPromptSaving] = useState(false);
  const [recommending, setRecommending] = useState(false);
  const [recommendation, setRecommendation] = useState('');

  // Shared Knowledge Base
  const [sharedKB, setSharedKB] = useState('');
  const [sharedKBSaving, setSharedKBSaving] = useState(false);
  const [syncingFiles, setSyncingFiles] = useState(false);

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [conversationsTotal, setConversationsTotal] = useState(0);
  const [conversationFilter, setConversationFilter] = useState('');
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [expandedConversation, setExpandedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);

  // Feature Routing
  const [featureAssignments, setFeatureAssignments] = useState({});
  const [featureSaving, setFeatureSaving] = useState({});
  const [featureEdits, setFeatureEdits] = useState({});

  const FEATURE_ICONS = {
    photo_generation: Camera,
    loi_verification: FileCheck,
    translation: Languages,
    company_description: Building2,
  };

  const primaryColor = theme?.primary_color || '#2B4B6A';

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'prompts' && agents[activePromptAgent]) {
      setPromptForm({
        system_prompt: agents[activePromptAgent]?.system_prompt || '',
        knowledge_base: agents[activePromptAgent]?.knowledge_base || '',
      });
      setRecommendation('');
    }
  }, [activeTab, activePromptAgent, agents]);

  useEffect(() => {
    if (activeTab === 'conversations') {
      fetchConversations();
    }
  }, [activeTab, conversationFilter]);

  useEffect(() => {
    if (activeTab === 'features') {
      fetchFeatureAssignments();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/intel/settings');
      setEnabled(res.data.enabled);
      setIntelPaidOnly(res.data.intel_paid_only || false);
      setModels(res.data.models || []);
      setAgents(res.data.agents || {});
      setSharedKB(res.data.shared_knowledge_base || '');
      // Provider list is owned by the backend; keep the fallback if this fails.
      api.get('/admin/intel/providers')
        .then(r => { if (r.data.providers?.length) setProviders(r.data.providers); })
        .catch(() => {});
    } catch (error) {
      console.error('Error fetching intel settings:', error);
      toast.error('Failed to load intel settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      setConversationsLoading(true);
      const params = conversationFilter ? `?agent_type=${conversationFilter}` : '';
      const res = await api.get(`/admin/intel/conversations${params}`);
      setConversations(res.data.conversations || []);
      setConversationsTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const fetchConversationDetail = async (sessionId) => {
    try {
      const res = await api.get(`/admin/intel/conversations/${sessionId}`);
      setConversationDetail(res.data);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  // ========== Feature Routing ==========
  const fetchFeatureAssignments = async () => {
    try {
      const res = await api.get('/admin/ai-model-settings');
      const features = res.data.features || {};
      setFeatureAssignments(features);
      // Initialize edits with current intel_model_id values
      const edits = {};
      Object.entries(features).forEach(([key, feat]) => {
        edits[key] = feat.intel_model_id || '';
      });
      setFeatureEdits(edits);
    } catch (error) {
      console.error('Error fetching feature assignments:', error);
    }
  };

  const handleSaveFeature = async (featureKey) => {
    const selectedModelId = featureEdits[featureKey] || '';
    setFeatureSaving(prev => ({ ...prev, [featureKey]: true }));
    try {
      await api.put(`/admin/ai-model-settings/${featureKey}`, {
        intel_model_id: selectedModelId,
      });
      toast.success(`${featureAssignments[featureKey]?.label || featureKey} updated`);
      await fetchFeatureAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feature');
    } finally {
      setFeatureSaving(prev => ({ ...prev, [featureKey]: false }));
    }
  };

  // ========== Global Toggle ==========
  const handleToggle = async () => {
    try {
      setToggling(true);
      const res = await api.put('/admin/intel/settings/toggle', { enabled: !enabled });
      setEnabled(res.data.enabled);
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to toggle intel system');
    } finally {
      setToggling(false);
    }
  };

  const handleTogglePaidOnly = async () => {
    try {
      setTogglingPaidOnly(true);
      const res = await api.put('/admin/intel/settings/intel-paid-only', { intel_paid_only: !intelPaidOnly });
      setIntelPaidOnly(res.data.intel_paid_only);
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to toggle intel paid-only setting');
    } finally {
      setTogglingPaidOnly(false);
    }
  };

  // ========== Model CRUD ==========
  const resetModelForm = () => {
    setModelForm({ name: '', provider: 'anthropic', model_id: '', api_key: '', api_endpoint: '' });
    setEditingModel(null);
    setShowModelForm(false);
    setProviderModels([]);
  };

  const handleSaveModel = async () => {
    if (!modelForm.name || !modelForm.model_id) {
      toast.error('Name and Model ID are required');
      return;
    }
    if (!editingModel && !modelForm.api_key && providers.find(p => p.value === modelForm.provider)?.needs_key !== false) {
      toast.error('API Key is required for new models');
      return;
    }
    try {
      setModelSaving(true);
      if (editingModel) {
        await api.put(`/admin/intel/models/${editingModel}`, modelForm);
        toast.success('Model updated');
      } else {
        await api.post('/admin/intel/models', modelForm);
        toast.success('Model added');
      }
      resetModelForm();
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save model');
    } finally {
      setModelSaving(false);
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (!window.confirm('Delete this model configuration?')) return;
    try {
      await api.delete(`/admin/intel/models/${modelId}`);
      toast.success('Model deleted');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete model');
    }
  };

  const handleTestModel = async (modelId) => {
    try {
      setTestingModel(modelId);
      const res = await api.post(`/admin/intel/models/${modelId}/test`);
      setTestedModels(prev => ({ ...prev, [modelId]: 'success' }));
      toast.success(res.data.message);
    } catch (error) {
      setTestedModels(prev => ({ ...prev, [modelId]: 'failed' }));
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingModel(null);
    }
  };

  const handleEditModel = (model) => {
    setEditingModel(model.id);
    setModelForm({
      name: model.name,
      provider: model.provider,
      model_id: model.model_id,
      api_key: '',
      api_endpoint: model.api_endpoint || '',
    });
    setShowModelForm(true);
  };

  // silent=true is the automatic load when the form opens; it must not shout at
  // the admin if a provider is unreachable, it just leaves the fallback list.
  const handleFetchProviderModels = useCallback(async (silent = false) => {
    const canUseSavedKey = Boolean(editingModel);
    if (!modelForm.api_key && !canUseSavedKey) {
      if (!silent) toast.error('Enter an API key first to fetch available models');
      return;
    }
    try {
      setFetchingProviderModels(true);
      const res = await api.post('/admin/intel/models/fetch-from-provider', {
        provider: modelForm.provider,
        // Blank when editing — the backend then reads the stored key for this config.
        api_key: modelForm.api_key || '',
        api_endpoint: modelForm.api_endpoint,
        model_config_id: editingModel || undefined,
      });
      setProviderModels(res.data.models || []);
      if (!silent) toast.success(`Found ${res.data.count} models from provider`);
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || 'Failed to fetch models from provider');
      setProviderModels([]);
    } finally {
      setFetchingProviderModels(false);
    }
  }, [modelForm.provider, modelForm.api_key, modelForm.api_endpoint, editingModel]);

  // Pull the live catalogue whenever the provider changes or an existing model
  // is opened, so the dropdown shows what the provider actually offers today.
  useEffect(() => {
    if (!showModelForm) return;
    if (modelForm.provider === 'claude_local') return;  // static alias list
    if (!editingModel && !modelForm.api_key) return;
    handleFetchProviderModels(true);
  }, [showModelForm, editingModel, modelForm.provider, handleFetchProviderModels, modelForm.api_key]);

  // ========== Agent Config ==========
  const handleAgentUpdate = async (agentType, updates) => {
    try {
      setAgentSaving(agentType);
      await api.put(`/admin/intel/agents/${agentType}`, updates);
      toast.success(`${AGENT_LABELS[agentType].name} updated`);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setAgentSaving(null);
    }
  };

  // ========== Prompt Management ==========
  const handleSaveSharedKB = async () => {
    try {
      setSharedKBSaving(true);
      await api.put('/admin/intel/settings/shared-knowledge-base', {
        shared_knowledge_base: sharedKB,
      });
      toast.success('Shared knowledge base saved');
    } catch (error) {
      toast.error('Failed to save shared knowledge base');
    } finally {
      setSharedKBSaving(false);
    }
  };

  const handleResetSharedKB = async () => {
    if (!window.confirm('Reset the shared knowledge base to factory defaults? Your customizations will be lost.')) return;
    try {
      setSharedKBSaving(true);
      const res = await api.post('/admin/intel/settings/reset-shared-kb');
      setSharedKB(res.data.shared_knowledge_base || '');
      toast.success('Shared knowledge base reset to defaults');
    } catch (error) {
      toast.error('Failed to reset shared knowledge base');
    } finally {
      setSharedKBSaving(false);
    }
  };

  const handleSyncToFiles = async () => {
    if (!window.confirm('This will overwrite the local .md files on the server with the current DB content. Continue?')) return;
    try {
      setSyncingFiles(true);
      const res = await api.post('/admin/intel/settings/sync-to-files');
      toast.success(`${res.data.message}: ${res.data.files.join(', ')}`);
    } catch (error) {
      toast.error('Failed to sync files');
    } finally {
      setSyncingFiles(false);
    }
  };

  const handleSavePrompt = async () => {
    try {
      setPromptSaving(true);
      await api.put(`/admin/intel/agents/${activePromptAgent}`, {
        system_prompt: promptForm.system_prompt,
        knowledge_base: promptForm.knowledge_base,
      });
      toast.success('Prompt & knowledge base saved');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save prompt');
    } finally {
      setPromptSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    const agentName = AGENT_LABELS[activePromptAgent]?.name || activePromptAgent;
    if (!window.confirm(
      `⚠️ WARNING: This will permanently replace ${agentName}'s custom prompt and knowledge base with the factory defaults.\n\nAll your customizations for ${agentName} will be lost.\n\nAre you sure?`
    )) return;
    try {
      setPromptSaving(true);
      const res = await api.post(`/admin/intel/agents/${activePromptAgent}/reset-defaults`);
      setPromptForm({
        system_prompt: res.data.system_prompt,
        knowledge_base: res.data.knowledge_base,
      });
      toast.success('Reset to defaults');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to reset defaults');
    } finally {
      setPromptSaving(false);
    }
  };

  const handleRecommendPrompt = async () => {
    try {
      setRecommending(true);
      setRecommendation('');
      const res = await api.post(`/admin/intel/agents/${activePromptAgent}/recommend-prompt`, {
        current_prompt: promptForm.system_prompt,
      });
      setRecommendation(res.data.recommendation);
      toast.success('Recommendation generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get recommendation');
    } finally {
      setRecommending(false);
    }
  };

  const applyRecommendation = () => {
    setPromptForm(prev => ({ ...prev, system_prompt: recommendation }));
    setRecommendation('');
    toast.success('Recommendation applied to editor. Click Save to keep it.');
  };

  // ========== Render ==========
  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
        </div>
      </Sidebar>
    );
  }

  const tabs = [
    { id: 'models', label: 'AI Models', icon: Brain },
    { id: 'agents', label: 'Agent Config', icon: Bot },
    { id: 'prompts', label: 'Prompts & Knowledge', icon: MessageSquare },
    { id: 'conversations', label: 'Conversation Logs', icon: History },
    { id: 'costs', label: 'Claude Costs', icon: DollarSign },
    { id: 'features', label: 'Feature Routing', icon: Cpu },
  ];

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Intel Settings</h1>
            <p className="text-gray-500 text-sm mt-1">Configure AI models, agents, and knowledge bases</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleTogglePaidOnly}
              disabled={togglingPaidOnly}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                intelPaidOnly ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title="When enabled, full intel analysis is only shown to paying customers"
            >
              {togglingPaidOnly ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {intelPaidOnly ? 'Intel: Paid Only' : 'Intel: Public'}
            </button>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : enabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
              {enabled ? 'System Active' : 'System Disabled'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-current'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === tab.id ? { color: primaryColor, borderColor: primaryColor } : {}}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'models' && (
          <div className="space-y-4">
            {/* Add Model Button */}
            <div className="flex justify-end">
              <button
                onClick={() => { resetModelForm(); setShowModelForm(true); }}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="w-4 h-4" /> Add Model
              </button>
            </div>

            {/* Model Form */}
            {showModelForm && (
              <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
                <h3 className="font-semibold text-lg">{editingModel ? 'Edit Model' : 'Add New Model'}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={modelForm.name}
                      onChange={e => setModelForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Claude Sonnet 4"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                    <select
                      value={modelForm.provider}
                      onChange={e => {
                        const newProvider = e.target.value;
                        const firstModel = SUGGESTED_MODELS[newProvider]?.[0]?.id || '';
                        const defaultEndpoint = providers.find(p => p.value === newProvider)?.default_endpoint || '';
                        setModelForm(prev => ({ ...prev, provider: newProvider, model_id: firstModel, api_endpoint: defaultEndpoint }));
                        setProviderModels([]);
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {providers.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">Model ID</label>
                      <button
                        type="button"
                        onClick={() => handleFetchProviderModels(false)}
                        disabled={fetchingProviderModels || !modelForm.api_key || modelForm.provider === 'claude_local'}
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 transition-colors"
                        title={!modelForm.api_key ? 'Enter an API key first' : 'Fetch available models from provider'}
                      >
                        {fetchingProviderModels ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Fetch Models
                      </button>
                    </div>
                    {/* Show fetched models from provider API if available */}
                    {providerModels.length > 0 ? (
                      <div className="space-y-2">
                        <select
                          value={providerModels.some(m => m.id === modelForm.model_id) ? modelForm.model_id : '_custom'}
                          onChange={e => {
                            if (e.target.value !== '_custom') {
                              setModelForm(prev => ({ ...prev, model_id: e.target.value }));
                            }
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-green-300"
                        >
                          {providerModels.map(m => (
                            <option key={m.id} value={m.id}>{m.name || m.id}</option>
                          ))}
                          <option value="_custom">Custom Model ID...</option>
                        </select>
                        <p className="text-xs text-green-600">{providerModels.length} models fetched from provider</p>
                        {!providerModels.some(m => m.id === modelForm.model_id) && (
                          <input
                            type="text"
                            value={modelForm.model_id}
                            onChange={e => setModelForm(prev => ({ ...prev, model_id: e.target.value }))}
                            placeholder="Enter custom model ID"
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        )}
                      </div>
                    ) : SUGGESTED_MODELS[modelForm.provider]?.length > 0 ? (
                      <div className="space-y-2">
                        <select
                          value={SUGGESTED_MODELS[modelForm.provider]?.some(m => m.id === modelForm.model_id) ? modelForm.model_id : '_custom'}
                          onChange={e => {
                            if (e.target.value !== '_custom') {
                              setModelForm(prev => ({ ...prev, model_id: e.target.value }));
                            }
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {SUGGESTED_MODELS[modelForm.provider].map(m => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                          <option value="_custom">Custom Model ID...</option>
                        </select>
                        {!SUGGESTED_MODELS[modelForm.provider]?.some(m => m.id === modelForm.model_id) && (
                          <input
                            type="text"
                            value={modelForm.model_id}
                            onChange={e => setModelForm(prev => ({ ...prev, model_id: e.target.value }))}
                            placeholder="Enter custom model ID"
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={modelForm.model_id}
                        onChange={e => setModelForm(prev => ({ ...prev, model_id: e.target.value }))}
                        placeholder="Enter model ID"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    )}
                  </div>
                  {modelForm.provider === 'claude_local' ? (
                    <div className="sm:col-span-2 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                      Runs the <code>claude</code> CLI installed on this server, using its Claude login — no API key needed.
                      Text-only: tools, temperature and max tokens are ignored.
                    </div>
                  ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                    <input
                      type="password"
                      value={modelForm.api_key}
                      onChange={e => setModelForm(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder={editingModel ? 'Leave blank to keep existing' : 'sk-...'}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  )}
                  {providers.find(p => p.value === modelForm.provider)?.needs_endpoint && (
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoint (Base URL)</label>
                      <input
                        type="text"
                        value={modelForm.api_endpoint}
                        onChange={e => setModelForm(prev => ({ ...prev, api_endpoint: e.target.value }))}
                        placeholder="https://api.moonshot.cn/v1"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={resetModelForm} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveModel}
                    disabled={modelSaving}
                    className="flex items-center gap-2 px-4 py-2 text-white rounded-lg"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {modelSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {editingModel ? 'Update' : 'Add'} Model
                  </button>
                </div>
              </div>
            )}

            {/* Models List */}
            {models.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center text-gray-500">
                <Brain className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No AI models configured</p>
                <p className="text-sm mt-1">Add a model to get started with the Intel system</p>
              </div>
            ) : (
              <div className="space-y-3">
                {models.map(model => (
                  <div key={model.id} className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                          style={{ backgroundColor: primaryColor }}>
                          {model.provider?.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium truncate">{model.name}</h4>
                          <p className="text-sm text-gray-500 truncate">
                            {providers.find(p => p.value === model.provider)?.label || model.provider} — {model.model_id}
                          </p>
                          {model.provider !== 'claude_local' && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              API Key: {showApiKeys[model.id] ? model.api_key_preview : '••••••••'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => setShowApiKeys(prev => ({ ...prev, [model.id]: !prev[model.id] }))}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded"
                          title="Toggle API key visibility"
                        >
                          {showApiKeys[model.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleTestModel(model.id)}
                          disabled={testingModel === model.id}
                          className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            testedModels[model.id] === 'success'
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : testedModels[model.id] === 'failed'
                              ? 'bg-red-100 text-red-600 hover:bg-red-200'
                              : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          }`}
                        >
                          {testingModel === model.id ? <Loader2 className="w-3 h-3 animate-spin" /> : testedModels[model.id] === 'success' ? <Check className="w-3 h-3" /> : testedModels[model.id] === 'failed' ? <X className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                          {testedModels[model.id] === 'success' ? 'Verified' : testedModels[model.id] === 'failed' ? 'Failed' : 'Test'}
                        </button>
                        <button
                          onClick={() => handleEditModel(model)}
                          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteModel(model.id)}
                          className="p-2 text-red-400 hover:text-red-600 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="space-y-4">
            {AGENT_ORDER.map(agentType => {
              const agent = agents[agentType] || {};
              const label = AGENT_LABELS[agentType];
              return (
                <div key={agentType} className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{label.icon}</span>
                      <div>
                        <h3 className="font-semibold text-lg">{label.name} <span className="text-sm font-normal text-gray-400">— {label.subtitle}</span></h3>
                        <p className="text-sm text-gray-500">{label.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAgentUpdate(agentType, { enabled: !agent.enabled })}
                      disabled={agentSaving === agentType}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        agent.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {agentSaving === agentType ? <Loader2 className="w-3 h-3 animate-spin" /> : agent.enabled ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {agent.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">AI Model</label>
                      <select
                        value={agent.model_id || ''}
                        onChange={e => handleAgentUpdate(agentType, { model_id: e.target.value || null })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="">— Select Model —</option>
                        {models.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temperature ({agent.temperature ?? 0.7})</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={agent.temperature ?? 0.7}
                        onChange={e => handleAgentUpdate(agentType, { temperature: parseFloat(e.target.value) })}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Precise</span>
                        <span>Creative</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                      <input
                        type="number"
                        value={localMaxTokens[agentType] ?? agent.max_tokens ?? 4096}
                        onChange={e => setLocalMaxTokens(prev => ({ ...prev, [agentType]: e.target.value }))}
                        onBlur={e => {
                          const val = parseInt(e.target.value) || 4096;
                          setLocalMaxTokens(prev => { const n = { ...prev }; delete n[agentType]; return n; });
                          handleAgentUpdate(agentType, { max_tokens: val });
                        }}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        min="256"
                        max="32768"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'prompts' && (
          <div className="space-y-4">
            {/* Shared Knowledge Base */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Shared Knowledge Base
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This content is shared across ALL agents (Amanda, Brandon, and Intel). Add universal information here — platform policies, terminology, company details, etc.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSyncToFiles}
                    disabled={syncingFiles}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                    title="Save all DB prompts and knowledge bases to local .md files on the server"
                  >
                    {syncingFiles ? <Loader2 className="w-3 h-3 animate-spin" /> : <HardDriveDownload className="w-3 h-3" />}
                    Sync to Local Files
                  </button>
                  <button
                    onClick={handleResetSharedKB}
                    disabled={sharedKBSaving}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                </div>
              </div>
              <textarea
                value={sharedKB}
                onChange={e => setSharedKB(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg font-mono text-sm resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={10}
                placeholder="Add shared knowledge that all agents should know..."
              />
              <div className="flex justify-end mt-3">
                <button
                  onClick={handleSaveSharedKB}
                  disabled={sharedKBSaving}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  {sharedKBSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Shared Knowledge
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Agent Sub-tabs */}
            <div className="flex gap-2 flex-wrap">
              {AGENT_ORDER.map(agentType => (
                <button
                  key={agentType}
                  onClick={() => setActivePromptAgent(agentType)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activePromptAgent === agentType ? 'text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'
                  }`}
                  style={activePromptAgent === agentType ? { backgroundColor: primaryColor } : {}}
                >
                  <span>{AGENT_LABELS[agentType].icon}</span>
                  {AGENT_LABELS[agentType].name}
                </button>
              ))}
            </div>

            {/* System Prompt */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-semibold">System Prompt</h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleRecommendPrompt}
                    disabled={recommending || !agents[activePromptAgent]?.model_id}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 disabled:opacity-50"
                    title={!agents[activePromptAgent]?.model_id ? 'Assign a model to this agent first' : 'Get AI recommendation'}
                  >
                    {recommending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    AI Recommend
                  </button>
                  <button
                    onClick={handleResetDefaults}
                    disabled={promptSaving}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset Defaults
                  </button>
                </div>
              </div>
              <textarea
                value={promptForm.system_prompt}
                onChange={e => setPromptForm(prev => ({ ...prev, system_prompt: e.target.value }))}
                className="w-full px-4 py-3 border rounded-lg font-mono text-sm resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={12}
                placeholder="Enter the system prompt for this agent..."
              />
            </div>

            {/* AI Recommendation */}
            {recommendation && (
              <div className="bg-purple-50 rounded-lg border border-purple-200 p-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> AI Recommendation
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={applyRecommendation}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      <Check className="w-3 h-3" /> Apply
                    </button>
                    <button
                      onClick={() => setRecommendation('')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white text-gray-600 rounded-lg hover:bg-gray-100 border"
                    >
                      <X className="w-3 h-3" /> Dismiss
                    </button>
                  </div>
                </div>
                <pre className="whitespace-pre-wrap text-sm text-purple-900 bg-white rounded-lg p-4 border max-h-64 overflow-y-auto">
                  {recommendation}
                </pre>
              </div>
            )}

            {/* Knowledge Base */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="font-semibold mb-3">Knowledge Base</h3>
              <p className="text-sm text-gray-500 mb-3">
                This content is appended to the system prompt as reference material. Include FAQs, platform details, trade terminology, etc.
              </p>
              <textarea
                value={promptForm.knowledge_base}
                onChange={e => setPromptForm(prev => ({ ...prev, knowledge_base: e.target.value }))}
                className="w-full px-4 py-3 border rounded-lg font-mono text-sm resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={16}
                placeholder="Paste your knowledge base content here..."
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSavePrompt}
                disabled={promptSaving}
                className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium"
                style={{ backgroundColor: primaryColor }}
              >
                {promptSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Prompt & Knowledge Base
              </button>
            </div>
          </div>
        )}

        {activeTab === 'conversations' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium text-gray-700">Filter by agent:</label>
              <select
                value={conversationFilter}
                onChange={e => setConversationFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">All Agents</option>
                <option value="public">Brandon (External)</option>
                <option value="user">Amanda (Internal)</option>
                <option value="intel">Intel</option>
              </select>
              <span className="text-sm text-gray-500">{conversationsTotal} conversations</span>
            </div>

            {/* Conversations List */}
            {conversationsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: primaryColor }} />
              </div>
            ) : conversations.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No conversations yet</p>
                <p className="text-sm mt-1">Conversations will appear here once users start chatting with agents</p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversations.map(conv => (
                  <div key={conv.id} className="bg-white rounded-lg shadow-sm border">
                    <button
                      onClick={() => {
                        if (expandedConversation === conv.session_id) {
                          setExpandedConversation(null);
                          setConversationDetail(null);
                        } else {
                          setExpandedConversation(conv.session_id);
                          fetchConversationDetail(conv.session_id);
                        }
                      }}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{AGENT_LABELS[conv.agent_type]?.icon || '?'}</span>
                        <div>
                          <p className="font-medium text-sm">
                            {AGENT_LABELS[conv.agent_type]?.name || conv.agent_type}
                            <span className="text-gray-400 font-normal ml-1">— {AGENT_LABELS[conv.agent_type]?.subtitle || ''}</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            {conv.message_count} messages — {new Date(conv.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {expandedConversation === conv.session_id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>
                    {expandedConversation === conv.session_id && conversationDetail && (
                      <div className="border-t p-4 space-y-3 max-h-96 overflow-y-auto bg-gray-50">
                        {conversationDetail.messages?.map((msg, idx) => (
                          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                              className={`max-w-[80%] rounded-lg px-4 py-2 text-sm ${
                                msg.role === 'user' ? 'bg-blue-100 text-blue-900' : 'bg-white border text-gray-800'
                              }`}
                            >
                              <p className="whitespace-pre-wrap">{msg.content}</p>
                              <p className="text-[10px] mt-1 opacity-50">
                                {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                                {msg.model_id ? ` — ${msg.model_id.substring(0, 8)}...` : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'costs' && <ClaudeCostTab primaryColor={primaryColor} />}

        {activeTab === 'features' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-2">
              <p className="text-sm text-blue-800">
                Assign an AI model from the <strong>AI Models</strong> tab to each platform feature. API keys are reused from the model configuration — no need to enter them again.
              </p>
            </div>

            {models.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center text-gray-500">
                <Cpu className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No AI Models Configured</p>
                <p className="text-sm mt-1">Go to the <strong>AI Models</strong> tab to add a model first, then come back here to assign it to features.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(featureAssignments).map(([key, feat]) => {
                  const Icon = FEATURE_ICONS[key] || Cpu;
                  const selectedModelId = featureEdits[key] || '';
                  const isSaving = featureSaving[key];
                  const currentModel = models.find(m => m.id === (feat.intel_model_id || ''));
                  const hasChanged = selectedModelId !== (feat.intel_model_id || '');

                  return (
                    <div key={key} className="bg-white rounded-lg shadow-sm border p-5">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className="p-2.5 rounded-lg shrink-0" style={{ backgroundColor: `${primaryColor}10` }}>
                          <Icon className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900">{feat.label}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">{feat.description}</p>
                          {feat.wired === false && (
                            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                              {feat.unwired_note || 'Not connected yet — assigning a model here has no effect.'}
                            </p>
                          )}

                          {/* Current assignment badge */}
                          {currentModel && (
                            <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                              <Check className="w-3 h-3" />
                              Currently: {currentModel.name} ({currentModel.provider} — {currentModel.model_id})
                            </div>
                          )}

                          {/* Model Selector */}
                          <div className="mt-3 flex items-center gap-3">
                            <select
                              value={selectedModelId}
                              onChange={e => setFeatureEdits(prev => ({ ...prev, [key]: e.target.value }))}
                              className="flex-1 max-w-md px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:outline-none"
                              style={{ borderColor: '#D1D5DB' }}
                            >
                              <option value="">— No model assigned —</option>
                              {models.map(m => (
                                <option key={m.id} value={m.id}>
                                  {m.name} ({m.provider} — {m.model_id})
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleSaveFeature(key)}
                              disabled={isSaving || !hasChanged}
                              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-40 hover:opacity-90"
                              style={{ backgroundColor: primaryColor }}
                            >
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminIntelSettings;
